import { Component, forwardRef, Inject, Input, OnInit } from '@angular/core';
import { TranslateService }         from '@ngx-translate/core';
import { NgbActiveModal }           from '@ng-bootstrap/ng-bootstrap';
import { UIRouter }                 from '@uirouter/core';

import { API }                               from '../api/api.constant';
import { InvoiceItemModel }                  from '../invoices/models/invoice.item';
import { InvoiceModel }                      from '../invoices/models/invoice';
import { InvoiceTypePipe }                   from '../invoices/invoice.type.pipe';
import { Table }                             from '../table/types/table';
import { TableQueryParameters }             from '../table/types/table-query-parameters';
import { TableRowEvent }                     from '../table/types/table-row-event';
import { VehicleHistoryActionsComponent }    from './vehicle.history.actions.component';
import { VehicleHistoryRowExpandComponent }  from './vehicle.history.row.expand.component';
import { VehicleHistoryService }             from './vehicle.history.service';
import { VehicleInvoiceItemsRowExpandComponent } from './vehicle.invoice-items.row.expand.component';
import { VehicleModel }                      from './models/vehicle';

@Component({
  selector   : 'wor-vehicle-history',
  template: require('./vehicle.history.component.html')
})
export class VehicleHistoryComponent implements OnInit {
  @Input() invoices: Array<InvoiceModel>;
  @Input() vehicle : VehicleModel;

  currentIndex      = 0;
  currentOffset     = 0;
  filterValue       : string = null;
  invoice           : InvoiceModel = null;
  invoiceItems      : Array<InvoiceItemModel> = [];
  isFlipped         = false;
  loading           = false;
  totalInvoiceCount = 0;
  updating          = false;

  itemsTableSettings: Table;
  tableSettings     : any;

  readonly next     = 1;
  readonly previous = -1;

  constructor (
    public  activeModal          : NgbActiveModal,
    private invoiceTypePipe      : InvoiceTypePipe,
    private router               : UIRouter,
    private translateService     : TranslateService,
    @Inject(forwardRef(() => VehicleHistoryService)) private vehicleHistoryService: VehicleHistoryService
  ) {}

  ngOnInit (): void {
    this.itemsTableSettings = {
      columns: [
        { field: 'item_code',    header: { text: 'Item Code' } },
        { field: 'description',  header: { text: 'Description' }, sortable: false },
        { classes: ['text-center'], field: 'quantity',   header: { text: 'Quantity'   }, pipe: 'number',         sortable: false },
        { classes: ['text-center'], field: 'unit_price', header: { text: 'Unit Price' }, pipe: 'globalCurrency', sortable: false },
        { classes: ['text-center'], field: 'amount',     header: { text: 'Total'      }, pipe: 'globalCurrency', sortable: false }
      ],
      empty: {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_ITEMS_FOUND')
      },
      pagination: {
        enabled   : true,
        pageSize  : 5,
        pageSizes : [ 5, 10, 25, 50 ],
        queryCount: () => this.vehicleHistoryService.getInvoiceItemsCount(this.invoice)
      },
      rows: {
        expand   : { enabled: true, factory: VehicleInvoiceItemsRowExpandComponent },
        highlight: { enabled: true }
      },
      sorting: { direction: 'asc', enabled: true, field: 'item_code' }
    };

    this.tableSettings = {
      columns: [
        { field: 'post_date',     header: { text: 'Date'           }, pipe: 'globalDate'  },
        { classes: ['text-center'], field: 'invoice_number', header: { text: 'Invoice Number' }, sortable: false },
        { field: 'invoice_type',  header: { text: 'Type'           }, pipe: 'invoiceType', sortable: false },
        { classes: ['text-center'], field: 'odometer', header: { text: 'Odometer' }, pipe: 'number', sortable: false },
        {
          component: {
            bindings: {
              outputs: {
                onCopyClick : (event) => this.copyInvoice(event.record, event.event),
                onEditClick : (event) => this.viewInvoice(event.record,  event.event),
                onPrintClick: (event) => this.printInvoice(event.record, event.event)
              }
            },
            enabled: true,
            factory: VehicleHistoryActionsComponent
          },
          sortable: false
        }
      ],
      empty: {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_HISTORY_FOUND')
      },
      pagination: {
        ...this.vehicleHistoryService.tableSettings.pagination,
        queryCount: () => this.getCount()
      },
      rows: {
        expand   : { enabled: true, factory: VehicleHistoryRowExpandComponent },
        highlight: { enabled: true },
        select   : { enabled: true }
      },
      sorting: this.vehicleHistoryService.tableSettings.sorting
    };
  }

  copyInvoice ( invoice: InvoiceModel, event: Event ): void {


    this.vehicleHistoryService.copyInvoice(invoice)
    .then(( copy: InvoiceModel ) => this.viewInvoice(copy));

    event.stopPropagation();


  }

  getCount (): Promise<number> {
    const request = this.filterValue
      ? this.vehicleHistoryService.searchCount(this.vehicle, this.filterValue)
      : this.vehicleHistoryService.getCount(this.vehicle);

    return request.then(( count: number ) => {
      this.totalInvoiceCount = count;
      return count;
    });
  }

  getInvoice ( value: number ): void {
    this.currentIndex += value;
    this.loading       = true;
    this.updating      = true;

    const params = {
      offset   : this.currentIndex,
      pageSize : 1,
      field    : this.tableSettings.sorting.field,
      direction: this.tableSettings.sorting.direction
    };

    const request = this.filterValue
      ? this.vehicleHistoryService.search(this.vehicle, params, this.filterValue)
      : this.vehicleHistoryService.getInvoices(this.vehicle, params);

    request
    .then(( invoices: Array<InvoiceModel> ) => {
      this.invoice = invoices[0];
      return this.getInvoiceItems();
    })
    .then(( items: Array<InvoiceItemModel> ) => {
      this.invoiceItems = items;
    })
    .finally(() => {
      this.updating = false;
      this.loading  = false;
    });
  }

  getInvoiceItems ( invoice = this.invoice ): Promise<Array<InvoiceItemModel>> {
    const params = {
      offset   : 0,
      pageSize : this.itemsTableSettings.pagination.pageSize,
      field    : this.itemsTableSettings.sorting.field,
      direction: this.itemsTableSettings.sorting.direction
    };

    return this.vehicleHistoryService.getInvoiceItems(invoice, params);
  }

  getInvoiceItemsOnQuery ( params: TableQueryParameters ): Promise<Array<InvoiceItemModel>> {
    return this.vehicleHistoryService.getInvoiceItems(this.invoice, params);
  }

  getInvoices ( params: TableQueryParameters ): Promise<Array<InvoiceModel>> {
    this.currentOffset = params.offset;

    const request = this.filterValue
      ? this.vehicleHistoryService.search(this.vehicle, params, this.filterValue)
      : this.vehicleHistoryService.getInvoices(this.vehicle, params);

    return request.then(( invoices: Array<InvoiceModel> ) => {
      this.invoices = invoices;
      return invoices;
    });
  }

  getInvoiceType ( type: string ): string {


    return this.invoiceTypePipe.transform(type);


  }

  isFirstInvoice (): boolean {


    return this.currentIndex === 0;


  }

  isLastInvoice (): boolean {


    return this.currentIndex === this.totalInvoiceCount - 1;


  }

  onRowDoubleClick ( event: TableRowEvent ): void {


    this.viewInvoiceItems(event.row);


  }

  printInvoice ( invoice: InvoiceModel, event: Event ): void {


    window.open(`${ API.url }/reports/jasper_invoice/${ invoice.id }`, '_blank');


    event.stopPropagation();


  }

  resetInvoices (): Promise<Array<InvoiceModel>> {
    this.updating    = true;
    this.filterValue = null;

    return this.getInvoices({
      offset   : 0,
      pageSize : this.tableSettings.pagination.pageSize,
      field    : this.tableSettings.sorting.field,
      direction: this.tableSettings.sorting.direction
    })
    .then(( invoices: Array<InvoiceModel> ) => {
      this.invoices = [];
      this.updating = false;

      return new Promise<Array<InvoiceModel>>(resolve => setTimeout(() => {
        this.invoices = invoices;
        resolve(invoices);
      }, 0));
    });
  }

  search (): void {
    this.updating = true;

    this.getInvoices({
      offset   : 0,
      pageSize : this.tableSettings.pagination.pageSize,
      field    : this.tableSettings.sorting.field,
      direction: this.tableSettings.sorting.direction
    })
    .finally(() => { this.updating = false; });
  }

  viewInvoice ( invoice: InvoiceModel, event?: Event ): void {
    this.router.stateService.go('app.invoice', {
      customer_id: invoice.customer_id,
      vehicle_id : invoice.vehicle_id,
      id         : invoice.id
    });

    this.activeModal.close();

    if (event) {


      event.stopPropagation();


      };


    }

  viewInvoiceItems ( invoice: InvoiceModel ): void {
    this.loading  = true;
    this.updating = true;
    this.invoice  = invoice;

    const currentIndex = this.invoices.map(inv => inv.id).indexOf(this.invoice.id);
    this.currentIndex  = currentIndex + this.currentOffset;

    this.getInvoiceItems()
    .then(( items: Array<InvoiceItemModel> ) => {
      this.isFlipped    = true;
      this.invoiceItems = items;
    })
    .finally(() => {
      this.updating = false;
      this.loading  = false;
    });
  }

  viewInvoices (): void {
    this.loading = true;

    this.resetInvoices()
    .then(() => {
      this.invoice      = null;
      this.invoiceItems = [];
      this.isFlipped    = false;
    })
    .finally(() => { this.loading = false; });
  }
}
